/**
 * StoneListingCard - Stone Investment themed Listing Card Component
 * 
 * Wraps ListingCardData with Stone Investment's luxury portfolio aesthetic:
 * - Greyscale palette with Provence/Mauritius flag colours on hover
 * - Cormorant Garamond (headings), Montserrat (body) typography
 * - HorizonOverlay effect with cinematic transitions
 * - Square corners on all images
 */

'use client';

import { WishlistButton } from '@/components/customer/WishlistButton';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import type { ListingCardData } from '@/components/listings/types';
import {
    getListingStatusLabel,
    getListingTypeLabel,
} from '@/components/listings/types';
import { FormatPrice } from '@/components/ui/FormatPrice';
import type { FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { ArrowUpRight, Bath, Bed, Calendar, Gavel, Maximize } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useState } from 'react';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

export interface StoneListingCardProps {
  listing: ListingCardData;
  href: string;
  variant?: 'default' | 'compact' | 'featured' | 'landscape' | 'portrait';
  showAgent?: boolean;
  showAgentAvatar?: boolean;
  showStatus?: boolean;
  showFeaturedBadge?: boolean;
  showWishlistButton?: boolean;
  wishlistEntityType?: 'PROPERTY' | 'LISTING';
  locale?: string;
  className?: string;
  messages?: Record<string, unknown>;
}

// Stone Investment themed colours for listing types
function getStoneListingTypeColor(type: string): string {
  if (type === 'SALE' || type === 'COMMERCIAL_SALE') {
    return 'bg-stone-900 text-stone-100';
  }
  if (type.includes('RENTAL') || type === 'ROOM_RENTAL' || type === 'SUBLET') {
    return 'bg-stone-700 text-stone-100';
  }
  if (type === 'AUCTION') {
    return 'bg-stone-800 text-stone-100';
  }
  if (type === 'LEASE' || type === 'COMMERCIAL_LEASE') {
    return 'bg-stone-600 text-stone-100';
  }
  return 'bg-stone-500 text-stone-100';
}

// Stone Investment themed colours for listing status
function getStoneListingStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-stone-400 text-stone-900',
    ACTIVE: 'bg-stone-900 text-stone-100',
    COMING_SOON: 'bg-stone-700 text-stone-100',
    UNDER_OFFER: 'bg-stone-600 text-stone-100',
    SOLD: 'bg-stone-900 text-white',
    LEASED: 'bg-stone-800 text-stone-100',
    WITHDRAWN: 'bg-stone-500 text-stone-100',
    ARCHIVED: 'bg-stone-400 text-stone-900',
    EXPIRED: 'bg-stone-400 text-stone-900',
  };
  return colors[status] || 'bg-stone-500 text-stone-100';
}

export const StoneListingCard = memo(function StoneListingCard({
  listing,
  href,
  variant = 'default',
  showAgent = false,
  showAgentAvatar = false,
  showStatus = true,
  showFeaturedBadge = true,
  showWishlistButton = true,
  wishlistEntityType = 'PROPERTY',
  locale = 'en',
  className = '',
  messages,
}: StoneListingCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const listingMessages = (stoneMessages.listing || {}) as Record<string, string>;
  
  const t = (key: string, fallback: string): string => {
    return listingMessages[key] || fallback;
  };
  
  const { property, location, listingType, status, price, auctionDetails, listingAgent, featuredPlacements } = listing;
  
  const wishlistEntityId = wishlistEntityType === 'LISTING' ? listing.listingId : property.id;
  const hasSpecialStatus = ['UNDER_OFFER', 'SOLD', 'LEASED', 'COMING_SOON'].includes(status);
  
  const primaryFeatured = showFeaturedBadge && featuredPlacements 
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;
  
  // Determine if we should use custom display text or format with FormatPrice
  const hasCustomPriceText = !!listing.displayPriceText;
  const isAuction = listingType === 'AUCTION';
  
  // Build location string including country for region detection
  const locationString = [
    location?.suburb,
    location?.city,
    location?.district,
    location?.neighbourhood,
    location?.region,
    location?.country,
  ].filter(Boolean).join(', ') || location?.name || t('locationTBA', 'Location TBA');

  // Display location (without country code)
  const displayLocation = [
    location?.suburb,
    location?.city,
    location?.district,
    location?.neighbourhood,
    location?.region || location?.country,
  ].filter(Boolean).join(', ') || location?.name || t('locationTBA', 'Location TBA');

  // Detect region for flag colours, using structured location data first
  const locationCountry = location?.country || '';
  const isFrance = locationCountry.toLowerCase() === 'france'
    || (!locationCountry && detectRegion(locationString) === 'provence');
  const countryLabel = locationCountry || (isFrance ? 'France' : '');

  const aspectRatio = variant === 'portrait' ? 'aspect-[3/4]' : 'aspect-video';

  return (
    <Link href={href} className="block no-underline text-inherit">
      <div
        className={`group relative w-full cursor-pointer flex flex-col gap-6 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image Container - Square corners for Stone aesthetic */}
        <div className={`relative w-full ${aspectRatio} overflow-hidden bg-stone-200`}>
          {/* Base Image with Zoom Effect */}
          {property.primaryImageUrl ? (
            <Image
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              loading="lazy"
              className={`object-cover transition-transform duration-[2000ms] ease-out will-change-transform ${
                isHovered ? 'scale-105' : 'scale-100'
              }`}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-stone-300 to-stone-200 flex items-center justify-center">
              <span className="text-stone-400 font-sans text-sm">{t('noImage', 'No Image')}</span>
            </div>
          )}

          {/* HorizonOverlay with flag colours */}
          <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />

          {/* Center Floating "View" Button */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
            <div 
              className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                isHovered ? 'w-24 h-24 opacity-100' : 'w-12 h-12 opacity-0 translate-y-4'
              }`}
            >
              <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
              <span className="text-white font-serif italic text-xl tracking-widest drop-shadow-md">
                {t('view', 'View')}
              </span>
            </div>
          </div>

          {/* Location Label - Bottom Left */}
          <div className="absolute bottom-6 left-6 z-20 overflow-hidden">
            <p 
              className={`text-[10px] uppercase tracking-[0.3em] text-stone-200 font-sans font-medium transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              {displayLocation}
            </p>
          </div>

          {/* Wishlist Button */}
          {showWishlistButton && !hasSpecialStatus && (
            <div className="absolute top-6 left-6 z-20">
              <WishlistButton
                entityType={wishlistEntityType}
                entityId={wishlistEntityId}
                variant="solid"
                size="md"
                className="bg-white/90 hover:bg-white shadow-sm"
              />
            </div>
          )}

          {/* Listing Type Badge */}
          <div className={`absolute ${showWishlistButton && !hasSpecialStatus ? 'top-6 left-16' : 'top-6 left-6'} px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-sans ${getStoneListingTypeColor(listingType)}`}>
            {getListingTypeLabel(listingType)}
          </div>

          {/* Featured Badge */}
          {primaryFeatured?.badgeVariant && !hasSpecialStatus && (
            <div className="absolute top-6 right-6 z-20">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="sm"
              />
            </div>
          )}

          {/* Status Badge */}
          {showStatus && hasSpecialStatus && (
            <div className={`absolute top-6 right-6 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-sans ${getStoneListingStatusColor(status)}`}>
              {getListingStatusLabel(status)}
            </div>
          )}

          {/* Auction Date Banner */}
          {listingType === 'AUCTION' && auctionDetails?.auctionDate && (
            <div className="absolute bottom-0 left-0 right-0 bg-stone-900 text-stone-100 px-4 py-2 flex items-center gap-2">
              <Gavel size={14} />
              <span className="text-xs font-sans tracking-wide">
                Auction: {new Date(auctionDetails.auctionDate).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
        </div>

        {/* Content Section - Editorial Typography */}
        <div className="flex flex-col gap-4 relative">
          {/* Title & Price Row */}
          <div className="flex justify-between items-baseline border-t border-stone-200 pt-6">
            <h3 className={`font-serif text-2xl md:text-3xl text-stone-900 leading-tight transition-all duration-500 ${isHovered ? 'italic' : ''}`}>
              {listing.mainHeadline || property.name}
            </h3>
            
            {/* Price */}
            <div className="font-sans text-sm font-medium text-stone-900 shrink-0 ml-4">
              {hasCustomPriceText ? (
                <span>{listing.displayPriceText}</span>
              ) : isAuction && auctionDetails ? (
                <span className="flex items-center gap-1">
                  {auctionDetails.guidePriceMin && (
                    <FormatPrice
                      amount={auctionDetails.guidePriceMin}
                      currency={auctionDetails.currency || DEFAULT_CURRENCY}
                      size="sm"
                      priceOnRequestText={t('guideTBA', 'Guide TBA')}
                    />
                  )}
                  {auctionDetails.guidePriceMin && auctionDetails.guidePriceMax && (
                    <span className="mx-1">-</span>
                  )}
                  {auctionDetails.guidePriceMax && (
                    <FormatPrice
                      amount={auctionDetails.guidePriceMax}
                      currency={auctionDetails.currency || DEFAULT_CURRENCY}
                      size="sm"
                      priceOnRequestText=""
                    />
                  )}
                </span>
              ) : price ? (
                <FormatPrice
                  amount={price.amount}
                  currency={price.currency}
                  frequency={price.frequency ?? null}
                  size="sm"
                  priceOnRequestText={t('priceOnRequest', 'Price on request')}
                  className="!text-stone-900"
                />
              ) : (
                <span>{t('priceOnRequest', 'Price on request')}</span>
              )}
            </div>
          </div>

          {/* Property Details */}
          <div className="flex flex-wrap items-center gap-4 text-stone-600 font-sans text-sm tracking-widest uppercase">
            {listing.propertyType && (
              <>
                <span className="text-stone-900 font-medium">{listing.propertyType.name}</span>
                <span className="w-px h-3 bg-stone-400" />
              </>
            )}
            
            {property.bedrooms !== null && property.bedrooms > 0 && (
              <span className="flex items-center gap-1.5">
                <Bed size={14} className="text-stone-400" />
                {property.bedrooms}
              </span>
            )}
            
            {property.bathrooms !== null && property.bathrooms > 0 && (
              <span className="flex items-center gap-1.5">
                <Bath size={14} className="text-stone-400" />
                {property.bathrooms}
              </span>
            )}
            
            {property.totalFloorArea !== null && property.totalFloorArea > 0 && (
              <span className="flex items-center gap-1.5">
                <Maximize size={14} className="text-stone-400" />
                {property.totalFloorArea.toLocaleString()} {property.totalFloorAreaUnit?.toLowerCase() || 'm²'}
              </span>
            )}
            
            {countryLabel && (
              <>
                <span className="w-px h-3 bg-stone-400" />
                <span className={`transition-colors duration-500 ${isFrance ? 'group-hover:text-blue-900' : 'group-hover:text-teal-800'}`}>
                  {countryLabel}
                </span>
              </>
            )}
          </div>

          {/* Available Date */}
          {listing.availableDate && (
            <div className="flex items-center gap-2 text-xs text-stone-500 font-sans">
              <Calendar size={12} />
              <span>
                {t('available', 'Available')}: {new Date(listing.availableDate).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}

          {/* Agent Info */}
          {showAgent && listingAgent && (
            <div className="flex items-center gap-3 pt-3 border-t border-stone-200">
              {listingAgent.avatar && showAgentAvatar ? (
                <Image
                  src={listingAgent.avatar}
                  alt={listingAgent.name}
                  width={36}
                  height={36}
                  className="object-cover object-top"
                />
              ) : (
                <div className="w-9 h-9 bg-stone-200 flex items-center justify-center">
                  <span className="text-stone-600 font-serif text-sm">{listingAgent.name.charAt(0)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-sans font-medium text-stone-900 truncate">{listingAgent.name}</p>
                {listingAgent.jobTitle && (
                  <p className="text-xs text-stone-500 truncate font-sans">{listingAgent.jobTitle}</p>
                )}
              </div>
            </div>
          )}

          {/* Arrow indicator on hover */}
          <div className={`absolute right-0 bottom-0 transition-all duration-500 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
            <ArrowUpRight size={24} className="text-stone-900" strokeWidth={1} />
          </div>
        </div>
      </div>
    </Link>
  );
});

export default StoneListingCard;
